import React, { useEffect, useState } from "react";
import { Users } from "lucide-react";

/**
 * LivePlayers — displays a believable live-player count badge.
 * The number is deterministic from a seed (so it does not jump around on
 * re-renders), always ≥ 9000, and slowly oscillates every 6-10 seconds to
 * give a "pulse of life" feel. Zero network calls.
 */

function hashSeed(s) {
  let h = 2166136261 >>> 0;
  const str = String(s || "default");
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function baseFor(seed) {
  // 9000 – 17999, locked per seed
  return 9000 + (seed % 9000);
}

export default function LivePlayers({ seed = "game", min = 9000, className = "" }) {
  const seedNum = hashSeed(seed);
  const anchor = Math.max(min, baseFor(seedNum));
  const [count, setCount] = useState(anchor + ((seedNum >> 3) % 120));

  useEffect(() => {
    let active = true;
    let timer;
    const tick = () => {
      if (!active) return;
      setCount((c) => {
        // Random walk of ±1 to ±8, bounded within anchor ± 400
        const delta = Math.floor(Math.random() * 16) - 8;
        let next = c + delta;
        const low = anchor - 200;
        const high = anchor + 400;
        if (next < low) next = low + Math.floor(Math.random() * 20);
        if (next > high) next = high - Math.floor(Math.random() * 20);
        return next;
      });
      timer = setTimeout(tick, 2500 + Math.floor(Math.random() * 3500));
    };
    timer = setTimeout(tick, 1500 + Math.floor(Math.random() * 2000));
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [anchor]);

  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/55 backdrop-blur-sm text-[9px] font-black tracking-wider text-emerald-300 border border-emerald-400/40 shadow-lg ${className}`}
      data-testid={`live-players-${seed}`}
      style={{ textShadow: "0 1px 2px rgba(0,0,0,.9)" }}
    >
      <span className="relative flex items-center">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping absolute" />
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 relative" />
      </span>
      <Users className="w-2.5 h-2.5" strokeWidth={3} />
      <span className="tabular-nums text-white">{count.toLocaleString("en-IN")}</span>
      <span className="opacity-80">live</span>
    </span>
  );
}
